import type React from 'react'
import { useImperativeHandle, useRef, useState } from 'react'
import type { PenAnchorPoint } from '@/lib/elements/types/pen-anchor-point'
import type { PenToolOverlayHandle } from '../types/pen-tool-overlay-props'

interface UsePenDrawingStateOptions {
  initialPoints?: PenAnchorPoint[]
  onPathCompletedRef: React.MutableRefObject<(points: PenAnchorPoint[], isClosed: boolean) => void>
  ref: React.ForwardedRef<PenToolOverlayHandle>
}

/**
 * The in-progress path: anchors, the active handle drag, cursor position and close-path state,
 * plus the reset/complete/commit operations every exit path shares. Also exposes the overlay's
 * imperative `commitAndDiscard` handle.
 */
export function usePenDrawingState({
  initialPoints,
  onPathCompletedRef,
  ref,
}: UsePenDrawingStateOptions) {
  const svgRef = useRef<SVGSVGElement>(null)

  // Use a mutable ref for points so event handlers always see the latest value
  // without stale closure issues (especially critical for the double-click handler).
  const pointsRef = useRef<PenAnchorPoint[]>(initialPoints ? [...initialPoints] : [])
  // Capture initialPoints count once at mount; used by the unmount cleanup to determine
  // whether any new nodes were actually drawn (vs. just pre-loaded continuation points).
  const initialPointsLenRef = useRef(initialPoints?.length ?? 0)
  const [, forceUpdate] = useState(0)

  const mousePos = useRef<{ x: number; y: number } | null>(null)
  const [mousePosState, setMousePosState] = useState<{ x: number; y: number } | null>(null)

  const dragRef = useRef<{
    pointIndex: number
    startX: number
    startY: number
    hasMoved: boolean
  } | null>(null)

  const [isNearFirst, setIsNearFirst] = useState(false)

  // Track spacebar so pointer events pass through to the canvas pan handler while space is held.
  const spaceHeldRef = useRef(false)

  /** Drops the in-progress path and re-renders an empty overlay. The double-click discard path
   *  passes `clearMousePos: false` — it has always left the `mousePos` ref as it was. */
  function resetDrawing(clearMousePos: boolean) {
    pointsRef.current = []
    dragRef.current = null
    if (clearMousePos) mousePos.current = null
    setMousePosState(null)
    setIsNearFirst(false)
    forceUpdate((n) => n + 1)
  }

  function completePath(pts: PenAnchorPoint[], closed: boolean) {
    if (pts.length < 2) return
    onPathCompletedRef.current(pts, closed)
    // Reset overlay for next path
    resetDrawing(true)
  }

  /** Commits the in-progress path as an open shape, unless no new nodes were added beyond the
   *  `initialPointsLen` continuation points (see the unmount cleanup in the pointer listeners). */
  function commitInProgressPath(initialPointsLen: number) {
    const pts = pointsRef.current

    if (pts.length >= 2 && pts.length > initialPointsLen) {
      onPathCompletedRef.current?.(pts, false)
      pointsRef.current = []
    }
  }

  // Exposed so cancelPenToolDrawing() can synchronously commit before flushQueue() is called
  // during page navigation, ensuring the shape lands in the ChangeQueue before exitCheckpoint.
  useImperativeHandle(
    ref,
    () => ({
      commitAndDiscard: () => commitInProgressPath(initialPointsLenRef.current),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  return {
    svgRef,
    pointsRef,
    initialPointsLenRef,
    forceUpdate,
    mousePos,
    mousePosState,
    setMousePosState,
    dragRef,
    isNearFirst,
    setIsNearFirst,
    spaceHeldRef,
    resetDrawing,
    completePath,
    commitInProgressPath,
  }
}

export type PenDrawingState = ReturnType<typeof usePenDrawingState>
